import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./Orders.css";
import { supabase } from "../../../supabase";
import '../../../styles/global.css';
import '../components/Profile.css';
import { FiCalendar, FiUser, FiPhone, FiChevronDown, FiStar, FiCreditCard } from 'react-icons/fi';

// Matches the real order-card layout (Profile/Products already use this same
// dark-navy .skeleton-shimmer pattern) instead of the old light-gray
// ContentLoader skeleton, which flashed jarringly against this page's dark theme.
function OrdersSkeleton() {
  return (
    <div className="orders-container">
      <div className="orders-scroll">
        <div className="orders-section-label">
          <span className="skeleton-shimmer" style={{ width: 110, height: 11, borderRadius: 4, display: 'inline-block' }} />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="order-card">
            <div className="order-header">
              <div className="order-header-left">
                <div className="skeleton-shimmer" style={{ width: 130, height: 15, borderRadius: 4, marginBottom: 8 }} />
                <div className="order-meta-row">
                  <div className="skeleton-shimmer" style={{ width: 95, height: 12, borderRadius: 4 }} />
                  <div className="skeleton-shimmer" style={{ width: 55, height: 19, borderRadius: 20 }} />
                </div>
              </div>
              <div className="skeleton-shimmer" style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Seeded like Home/Coupons: paint the last-known order list instantly on a repeat
// visit instead of showing the skeleton every time, while the real fetch (below)
// re-runs in the background and replaces it once it resolves.
const getCachedOrders = () => {
  try { return JSON.parse(localStorage.getItem('pws_orders_cache')); } catch { return null; }
};

export default function Orders() {
  const { t } = useTranslation('orders');
  const cachedOrders = getCachedOrders();
  const canUseCache = localStorage.getItem('pws_auth') === '1' && Array.isArray(cachedOrders);
  const [orders, setOrders] = useState(canUseCache ? cachedOrders : []);
  const [openIndex, setOpenIndex] = useState(null);
  const [loading, setLoading] = useState(!canUseCache);
  const [error, setError] = useState(null);

  const loadOrders = useCallback(async (userId) => {
    try {
      const { data: userRow } = await supabase.from('users').select('id, points').eq('auth_user_id', userId).maybeSingle();
      if (!userRow) { setOrders([]); setLoading(false); return; }

      const { data: orderRows, error: ordersError } = await supabase
        .from('orders').select('*, order_items(*)').eq('user_id', userRow.id);
      if (ordersError) throw ordersError;

      const fetchedOrders = (orderRows || [])
        .map(order => ({
          id: order.id,
          orderReference: order.order_reference,
          datePurchased: order.date_purchased,
          totalPrice: order.total_price,
          buyerName: order.buyer_name,
          phoneNumber: order.phone_number,
          discountCode: order.discount_code,
          discountAmount: order.discount_amount,
          fullyRefunded: order.fully_refunded,
          refundedItems: order.refunded_items || {},
          paymentMethod: order.payment_method,
          fetchedProducts: (order.order_items || []).map(item => ({
            name: item.name, price: item.price, quantity: item.quantity, _type: item.category,
          })),
        }))
        .sort((a, b) => new Date(b.datePurchased || 0) - new Date(a.datePurchased || 0));

      setOrders(fetchedOrders);
      try { localStorage.setItem('pws_orders_cache', JSON.stringify(fetchedOrders)); } catch {}
      return userRow.id;
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(t('loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let channel = null;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const user = session?.user;
      setError(null);

      if (channel) { supabase.removeChannel(channel); channel = null; }

      if (!user) {
        setOrders([]);
        setLoading(false);
        localStorage.removeItem('pws_orders_cache');
        return;
      }

      const dbUserId = await loadOrders(user.id);
      if (!dbUserId) return;

      // Live refresh: an admin issuing a refund (or capture completing) updates
      // this same row - without this, the customer would only see the change
      // after leaving and re-entering the page.
      channel = supabase
        .channel('own_orders_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `user_id=eq.${dbUserId}` },
          () => loadOrders(user.id))
        .subscribe();
    });

    return () => {
      subscription.unsubscribe();
      if (channel) supabase.removeChannel(channel);
    };
  }, [loadOrders]);

  const toggleDropdown = (idx) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('unknown');
    const d = new Date(dateStr);
    const day   = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year  = String(d.getFullYear()).slice(-2);
    const hour  = String(d.getHours()).padStart(2, '0');
    const min   = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} kl. ${hour}:${min}`;
  };

  const getOrderTotal = (order) => {
    return Math.max(0, order.totalPrice || 0);
  };

  const getLineItems = (order) => {
    return (order.fetchedProducts || []).map(item => {
      // Keyed exactly like pws-con's refund tracking (order_reference_itemName,
      // against the item's raw name - not the "Billett:"/"Klippekort:" prefixed
      // display name below) - refund-payment writes into orders.refunded_items
      // under this same key.
      const key = `${order.orderReference}_${item.name}`;
      const refundedQty = order.refundedItems?.[key] || 0;
      return {
        qty: item.quantity || 1,
        name: item._type === 'klippekort' ? t('klippekortItemName', { name: item.name }) : t('ticketItemName', { name: item.name }),
        price: (item.price || 0) * (item.quantity || 1),
        refundedQty,
      };
    });
  };

  if (loading) {
    return <OrdersSkeleton />;
  }

  if (error) {
    return (
      <div className="orders-container">
        <p className="orders-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="orders-container">
      <div className="orders-scroll">
        <div className="orders-section-label"><span>{t('orderHistory')}</span></div>

        {orders.length === 0 ? (
          <div className="orders-empty">
            <p>{t('noOrdersFound')}</p>
            <p className="orders-tip">{t('noOrdersTip')}</p>
          </div>
        ) : orders.map((order, idx) => {
          const isOpen   = openIndex === idx;
          const total    = getOrderTotal(order);
          const points   = (total * 0.04).toFixed(2);
          const lineItems = getLineItems(order);

          return (
            <div key={order.id || idx} className={`order-card${isOpen ? ' open' : ''}`}>
              {/* Header */}
              <div className="order-header" onClick={() => toggleDropdown(idx)}>
                <div className="order-header-left">
                  <div className="order-ref">{order.orderReference ? `OR-${order.orderReference.substring(0, 8)}` : (order.id || t('unknown'))}</div>
                  <div className="order-meta-row">
                    <span className="order-date"><FiCalendar size={11} />{formatDate(order.datePurchased || order.createdAt)}</span>
                    <span className="order-total-chip">{total} kr</span>
                    {order.paymentMethod && (
                      <span className="order-payment-chip">
                        <FiCreditCard size={10} />
                        {order.paymentMethod === 'CARD' ? t('paymentCard') : t('paymentVipps')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="order-chevron"><FiChevronDown size={18} /></div>
              </div>

              {/* Body */}
              {isOpen && (
                <div className="order-body-inner">
                  {/* Customer info */}
                  <div className="order-customer-info">
                    {(order.buyerName || order.name) && (
                      <div className="order-info-row"><FiUser size={13} /><span>{t('nameLabel')}</span><strong>{order.buyerName || order.name}</strong></div>
                    )}
                    {(order.phoneNumber || order.phone) && (
                      <div className="order-info-row"><FiPhone size={13} /><span>{t('phoneLabel')}</span><strong>{order.phoneNumber || order.phone}</strong></div>
                    )}
                    {order.paymentMethod && (
                      <div className="order-info-row"><FiCreditCard size={13} /><span>{t('paymentMethodLabel')}</span><strong>{order.paymentMethod === 'CARD' ? t('paymentCard') : t('paymentVipps')}</strong></div>
                    )}
                  </div>

                  {/* Line items */}
                  <div className="order-items-label">{t('productsPurchased')}</div>
                  {order.fullyRefunded && (
                    <div className="order-refund-notice">{t('fullyRefundedNotice')}</div>
                  )}
                  {lineItems.length > 0 ? lineItems.map((item, i) => (
                    <div key={i} className="order-line-item">
                      <div className="order-line-left">
                        <div className="order-qty-badge">{item.qty}</div>
                        <div>
                          <span>{item.name}</span>
                          {item.refundedQty > 0 && (
                            <div className="order-line-refunded">{t('refunded')}</div>
                          )}
                        </div>
                      </div>
                      <strong>{item.price} kr</strong>
                    </div>
                  )) : (
                    <div className="order-line-item order-line-item--empty">
                      {t('totalNoDetails', { total })}
                    </div>
                  )}
                  {order.discountAmount > 0 && (
                    <div className="order-line-item" style={{ color: '#e74c3c' }}>
                      <div className="order-line-left">
                        <div className="order-qty-badge" style={{ background: '#e74c3c', color: '#fff' }}>🏷</div>
                        <span>{t('discountCode', { code: order.discountCode })}</span>
                      </div>
                      <strong>-{order.discountAmount} kr</strong>
                    </div>
                  )}

                  <div className="order-divider" />

                  <div className="order-total-row">
                    <span>{t('total')}</span>
                    <strong>{total} kr</strong>
                  </div>

                  <div className="order-points-banner">
                    <FiStar size={14} />
                    {t('pointsEarnedOnOrder', { points })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}